import { z } from 'zod';

// Teto diário de consultas PAGAS por usuário (#222).
//
// As abas Dados (cadastro do CNPJ) e Pessoas (contatos) chamam APIs cobradas por
// consulta. Até aqui o único controle era "uma por empresa, para sempre" — o que
// impede pagar duas vezes pela MESMA empresa, mas não impede um vendedor
// consultar duzentas empresas diferentes num dia.
//
// Este contrato é o teto por vendedor e por dia. Ele mora em `packages/types`
// porque a MESMA decisão precisa valer nos dois lados: o servidor recusa a
// consulta, e a UI mostra o saldo antes do clique. Duas cópias divergindo dariam
// botão habilitado que o servidor rejeita — a lição do `crmLeadCnpj`.

/**
 * O que está sendo consultado. Cotas SEPARADAS por tipo (ordem do owner): as
 * duas abas têm custo e frequência de uso diferentes, e um vendedor que
 * conferiu dez cadastros na Receita não deve por isso ficar sem os contatos.
 */
export const CrmLookupKindSchema = z.enum(['PEOPLE', 'DATA']);
export type CrmLookupKind = z.infer<typeof CrmLookupKindSchema>;

/** Doc de configuração — o número é ajustável sem deploy. */
export const CRM_LOOKUP_LIMITS_DOC_ID = 'lookupLimits';

/**
 * Teto por dia, por usuário, por tipo.
 *
 * `0` é válido e significa **bloqueado** — é o kill switch quando o gasto
 * dispara ou a chave do fornecedor expira. O máximo de 1000 não é um limite de
 * produto: é o que impede um zero a mais digitado na configuração virar uma
 * fatura de cinco dígitos.
 */
export const CrmLookupLimitsSchema = z.object({
  people: z.number().int().min(0).max(1000).default(5),
  data: z.number().int().min(0).max(1000).default(10),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type CrmLookupLimits = z.infer<typeof CrmLookupLimitsSchema>;

/** Valem quando o doc não existe, não valida, ou não pôde ser lido. */
export const CRM_LOOKUP_LIMITS_DEFAULT: Readonly<Record<CrmLookupKind, number>> = {
  PEOPLE: 5,
  DATA: 10,
};

/**
 * Contador de um usuário num dia. Um doc por dia — não há o que expirar à mão.
 *
 * Guarda DUAS coisas com propósitos diferentes: quantas consultas foram feitas
 * (o teto, acima) e **quanto elas custaram** — em cents inteiros, e por tipo.
 *
 * O custo em cents, e não em reais, é a regra do projeto para dinheiro nosso:
 * somar `0.29 + 0.74 + 0.74` em ponto flutuante acumula erro num número que
 * ninguém pode corrigir depois (as rules negam escrita a todos). O `cost` dos
 * documentos de `crm_research` continua float e VERBATIM — é a resposta do
 * fornecedor, guardada como veio; aqui é agregação nossa.
 *
 * Os campos de custo são gravados **inclusive para quem é isento do teto**
 * (ADMIN): custo é fato contábil, não limite. Por isso um doc de dia pode
 * existir com custo e contadores zerados.
 */
export const CrmLookupQuotaDaySchema = z.object({
  people: z.number().int().nonnegative().default(0),
  data: z.number().int().nonnegative().default(0),
  peopleCostCents: z.number().int().nonnegative().default(0),
  dataCostCents: z.number().int().nonnegative().default(0),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmLookupQuotaDay = z.infer<typeof CrmLookupQuotaDaySchema>;

/**
 * `crm_lookup_quota/{uid}/days/{dayKey}` — subcoleção, e não um doc
 * `{uid}_{dayKey}` como o contador de atividade.
 *
 * A diferença é a rule: com o uid no caminho, "só o dono lê o próprio saldo"
 * é `request.auth.uid == uid`, sem partir string de docId. E o dono PRECISA
 * ler: o saldo aparece na tela antes do clique.
 */
export const CRM_LOOKUP_QUOTA_COLLECTION = 'crm_lookup_quota';
export const CRM_LOOKUP_QUOTA_DAYS = 'days';

/** Campo do contador correspondente ao tipo de consulta. */
export function crmLookupQuotaField(kind: CrmLookupKind): 'people' | 'data' {
  return kind === 'PEOPLE' ? 'people' : 'data';
}

/** Campo do custo acumulado correspondente ao tipo de consulta. */
export function crmLookupQuotaCostField(
  kind: CrmLookupKind,
): 'peopleCostCents' | 'dataCostCents' {
  return kind === 'PEOPLE' ? 'peopleCostCents' : 'dataCostCents';
}

/**
 * Quanto o usuário gastou no dia, somando os dois tipos.
 *
 * A tela mostra UM número ("Você gastou R$ 3,51 hoje") em vez de um por aba: o
 * vendedor quer saber o que saiu da conta, não como se reparte entre cadastros e
 * contatos. A separação por tipo existe no armazenamento porque ela é grátis e
 * permite análise depois — não porque a tela precise dela.
 */
export function crmLookupDaySpendCents(day: CrmLookupQuotaDay): number {
  return day.peopleCostCents + day.dataCostCents;
}

/**
 * Teto defensivo de uma consulta, em cents (R$ 100).
 *
 * Não é regra de produto: é o que impede um valor absurdo do fornecedor (ou um
 * campo que mudou de unidade sem aviso) entrar num acumulado que ninguém
 * consegue corrigir pela UI — as rules negam escrita no contador a TODOS, então
 * número envenenado é permanente. Mesma paranoia do `max(1000)` do teto diário.
 */
export const CRM_LOOKUP_COST_CENTS_MAX = 10_000;

/**
 * Custo cobrado (em BRL, como o fornecedor manda) → cents inteiros.
 *
 * Ponto único de conversão, usado pelo servidor (para acumular) e pela UI (para
 * exibir o valor de cada consulta). Se cada lado arredondasse por conta própria,
 * um `0.745` viraria "R$ 0,75" no bloco e 74 cents no total do dia — duas linhas
 * da mesma tela que não fecham.
 *
 * Ausência, `NaN`, infinito e negativo viram 0: consulta sem custo informado não
 * é consulta de graça nem consulta com desconto, é consulta que não entra na
 * conta (ver a decisão do 404 da aba Dados, em `crmFetchCompanyData`).
 */
export function crmLookupCostCents(costBrl: number | null | undefined): number {
  if (costBrl == null || !Number.isFinite(costBrl) || costBrl <= 0) return 0;
  // `Math.round` e não `Math.trunc`: o fornecedor manda 2 casas, e `0.29 * 100`
  // dá 28.999… em ponto flutuante.
  return Math.min(Math.round(costBrl * 100), CRM_LOOKUP_COST_CENTS_MAX);
}

/** Teto configurado para o tipo, caindo no default quando não há config. */
export function crmLookupLimitFor(
  limits: CrmLookupLimits | null | undefined,
  kind: CrmLookupKind,
): number {
  if (limits == null) return CRM_LOOKUP_LIMITS_DEFAULT[kind];
  return kind === 'PEOPLE' ? limits.people : limits.data;
}

export type CrmLookupQuotaState = {
  used: number;
  limit: number;
  /** Nunca negativo: um teto reduzido depois do uso não vira saldo negativo na tela. */
  remaining: number;
  exhausted: boolean;
};

/**
 * Saldo do dia — função pura, usada pela UI (para mostrar) e pelo servidor
 * (para recusar). É o único lugar onde "acabou" é definido.
 */
export function crmLookupQuotaState(used: number, limit: number): CrmLookupQuotaState {
  const safeUsed = Number.isFinite(used) && used > 0 ? Math.floor(used) : 0;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 0;
  return {
    used: safeUsed,
    limit: safeLimit,
    remaining: Math.max(0, safeLimit - safeUsed),
    exhausted: safeUsed >= safeLimit,
  };
}

/**
 * ADMIN não tem teto (ordem do owner): é quem paga a conta e quem precisa
 * investigar um caso sem esbarrar no próprio limite. AUDITOR nem chega aqui —
 * é barrado antes, nas duas callables.
 */
export function crmLookupQuotaExempt(role: string): boolean {
  return role === 'ADMIN';
}
